import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import {
  ConfirmDialog,
  ModuleHeader
} from 'components/modals';
import type { ActionButton, ViewModeOption } from 'components/modals';
import {
  ActionMenu,
  type ActionMenuItem,
  SyutGrid,
  type SyutGridColumn,
  SyutSelect,
  SyutSearchGrid,
  MinimizedModalStrip,
} from 'components/ui';
import notificationService from 'services/notificationService';
import { useModalMinimize } from 'contexts/ModalMinimizeContext';
import { useCorporates, useDeleteCorporate } from 'hooks/api/useCorporate';
import type { Corporate, CorporateSearchRequest } from 'api/types';
import type { SelectOption } from 'components/ui/SyutSelect';
import CorporateModal from 'components/modals/modules/corporate-module/CorporateModal';
import CorporateViewModal from 'components/modals/modules/corporate-module/CorporateViewModal';

// ============================================================================
// TYPE DEFINITIONS
// ============================================================================

type ViewMode = 'list' | 'grid';

interface ColumnDefinition {
  id: string;
  label: string;
  field: string;
  required: boolean;
  defaultVisible: boolean;
  width?: number;
  sortable?: boolean;
}

// ============================================================================
// DROPDOWN OPTIONS
// ============================================================================

const statusOptions = [
  { value: '', label: 'All Status' },
  { value: 'true', label: 'Active' },
  { value: 'false', label: 'Inactive' },
];

const industryFilterOptions: SelectOption[] = [
  { value: '', label: 'All Industries' },
  { value: 'IT', label: 'IT' },
  { value: 'Manufacturing', label: 'Manufacturing' },
  { value: 'Healthcare', label: 'Healthcare' },
  { value: 'Finance', label: 'Finance' },
  { value: 'Education', label: 'Education' },
  { value: 'Retail', label: 'Retail' },
  { value: 'Hospitality', label: 'Hospitality' },
  { value: 'Government', label: 'Government' },
  { value: 'Other', label: 'Other' },
];

const discountTypeFilterOptions: SelectOption[] = [
  { value: '', label: 'All Discount Types' },
  { value: 'Percentage', label: 'Percentage' },
  { value: 'Flat Amount', label: 'Flat Amount' },
];

// ============================================================================
// COLUMN DEFINITIONS
// ============================================================================

const columnDefinitions: ColumnDefinition[] = [
  { id: 'select', label: '', field: 'select', required: true, defaultVisible: true, width: 50, sortable: false },
  { id: 'corporateCode', label: 'Corporate Code', field: 'corporateCode', required: false, defaultVisible: true, sortable: true },
  { id: 'companyName', label: 'Company Name', field: 'companyName', required: false, defaultVisible: true, sortable: true },
  { id: 'industry', label: 'Industry', field: 'industry', required: false, defaultVisible: true, sortable: true },
  { id: 'contactPerson', label: 'Contact Person', field: 'contactPerson', required: false, defaultVisible: false, sortable: true },
  { id: 'phone', label: 'Phone', field: 'phone', required: false, defaultVisible: false, sortable: false },
  { id: 'email', label: 'Email', field: 'email', required: false, defaultVisible: false, sortable: false },
  { id: 'creditDays', label: 'Credit Days', field: 'creditDays', required: false, defaultVisible: false, sortable: true },
  { id: 'creditLimit', label: 'Credit Limit', field: 'creditLimit', required: false, defaultVisible: false, sortable: true },
  { id: 'agreementStart', label: 'Agreement Start', field: 'agreementStart', required: false, defaultVisible: true, sortable: true },
  { id: 'agreementEnd', label: 'Agreement End', field: 'agreementEnd', required: false, defaultVisible: true, sortable: true },
  { id: 'discountType', label: 'Discount Type', field: 'discountType', required: false, defaultVisible: true, sortable: true },
  { id: 'discountValue', label: 'Discount Value', field: 'discountValue', required: false, defaultVisible: true, sortable: true },
  { id: 'isActive', label: 'Status', field: 'isActive', required: false, defaultVisible: true, sortable: true },
  { id: 'actions', label: 'Actions', field: 'actions', required: true, defaultVisible: true, width: 80, sortable: false },
];

// ============================================================================
// COLUMN SELECTOR COMPONENT
// ============================================================================

interface ColumnSelectorProps {
  columns: ColumnDefinition[];
  visibleColumns: string[];
  onApply: (visibleColumns: string[]) => void;
  onReset: () => void;
}

const ColumnSelector: React.FC<ColumnSelectorProps> = ({
  columns,
  visibleColumns: initialVisible,
  onApply,
  onReset,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedColumns, setSelectedColumns] = useState<string[]>(initialVisible);
  const [confirmResetOpen, setConfirmResetOpen] = useState(false);
  const panelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (panelRef.current && !panelRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setSelectedColumns(initialVisible);
      }
    };
    if (isOpen) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen, initialVisible]);

  const toggleColumn = (columnId: string) => {
    const column = columns.find(col => col.id === columnId);
    if (column?.required) return;
    setSelectedColumns(prev =>
      prev.includes(columnId) ? prev.filter(id => id !== columnId) : [...prev, columnId]
    );
  };

  const handleSelectAll = () => {
    const allColumnIds = columns.filter(col => !col.required).map(col => col.id);
    const requiredIds = columns.filter(col => col.required).map(col => col.id);
    if (selectedColumns.length === columns.length) {
      setSelectedColumns(requiredIds);
    } else {
      setSelectedColumns([...requiredIds, ...allColumnIds]);
    }
  };

  return (
    <div style={{ position: 'relative', display: 'inline-block' }} ref={panelRef}>
      <button
        className={`btn-icon ${isOpen ? 'active' : ''}`}
        onClick={() => setIsOpen(!isOpen)}
        title="Manage Columns"
        style={isOpen ? { background: 'var(--accent-gradient)', color: 'white', borderColor: 'var(--primary)' } : {}}
      >
        <IconSystem.grid size={18} />
      </button>

      {isOpen && (
        <div style={{
          position: 'absolute', top: 'calc(100% + 0.5rem)', right: 0, width: '320px', maxHeight: '500px',
          background: 'var(--bg-primary)', border: '2px solid var(--primary)', borderRadius: '0.75rem',
          boxShadow: '0 12px 32px rgba(0, 0, 0, 0.15)', zIndex: 1000, animation: 'slideDown 0.2s ease',
          display: 'flex', flexDirection: 'column', overflow: 'hidden',
        }}>
          <div style={{
            padding: '1rem 1.25rem', borderBottom: '2px solid var(--border-color)', background: 'var(--bg-secondary)',
            borderRadius: '0.75rem 0.75rem 0 0', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexShrink: 0,
          }}>
            <h3 style={{ fontSize: '0.95rem', fontWeight: 600, color: 'var(--text-primary)', margin: 0 }}>Manage Columns</h3>
            <button onClick={() => setIsOpen(false)}
              style={{ width: '1.5rem', height: '1.5rem', border: 'none', background: 'transparent', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: '0.25rem' }}>
              <IconSystem.x size={16} />
            </button>
          </div>

          <div style={{ flex: 1, overflowY: 'auto', padding: '0.75rem 0', minHeight: 0 }}>
            {columns.map(column => (
              <div key={column.id} onClick={() => toggleColumn(column.id)}
                style={{ display: 'flex', alignItems: 'center', padding: '0.625rem 1.25rem', cursor: column.required ? 'not-allowed' : 'pointer', transition: 'all 0.2s ease', gap: '0.75rem', opacity: column.required ? 0.6 : 1 }}>
                <div style={{
                  width: '1.125rem', height: '1.125rem', display: 'flex', alignItems: 'center', justifyContent: 'center',
                  border: '2px solid var(--border-color)', borderRadius: '0.25rem', transition: 'all 0.2s ease', flexShrink: 0,
                  ...(selectedColumns.includes(column.id) ? { background: 'var(--accent-gradient)', borderColor: 'var(--primary)', color: 'white' } : {}),
                }}>
                  {selectedColumns.includes(column.id) && <IconSystem.check size={14} />}
                </div>
                <span style={{ flex: 1, fontSize: '0.85rem', color: 'var(--text-primary)', userSelect: 'none' }}>
                  {column.label}{column.required && ' *'}
                </span>
              </div>
            ))}
          </div>

          <div style={{ padding: '1rem', borderTop: '2px solid var(--border-color)', background: 'var(--bg-primary)', boxShadow: '0 -4px 12px rgba(0, 0, 0, 0.05)', display: 'flex', flexDirection: 'column', gap: '0.75rem', flexShrink: 0 }}>
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <button onClick={handleSelectAll}
                style={{ flex: 1, padding: '0.5rem 0.75rem', fontSize: '0.75rem', background: 'var(--bg-primary)', border: '1px solid var(--border-color)', borderRadius: '0.5rem', cursor: 'pointer' }}>
                {selectedColumns.length === columns.length ? 'Deselect All' : 'Select All'}
              </button>
              <button onClick={() => setConfirmResetOpen(true)}
                style={{ flex: 1, padding: '0.5rem 0.75rem', fontSize: '0.75rem', background: 'var(--bg-primary)', border: '1px solid var(--border-color)', borderRadius: '0.5rem', cursor: 'pointer' }}>
                Reset Default
              </button>
            </div>
            <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
              <button className="btn btn-secondary" onClick={() => { setSelectedColumns(initialVisible); setIsOpen(false); }}>Cancel</button>
              <button className="btn btn-primary" onClick={() => { onApply(selectedColumns); setIsOpen(false); }}>Apply</button>
            </div>
          </div>
        </div>
      )}

      <ConfirmDialog open={confirmResetOpen} title="Reset Columns" message="Reset to default column configuration?"
        confirmLabel="Reset" cancelLabel="Cancel"
        onConfirm={() => { onReset(); setConfirmResetOpen(false); setIsOpen(false); }}
        onCancel={() => setConfirmResetOpen(false)} />
    </div>
  );
};

// ============================================================================
// CORPORATE CARD COMPONENT (Grid View)
// ============================================================================

interface CorporateCardProps {
  corporate: Corporate;
  onView: (c: Corporate) => void;
  onEdit: (c: Corporate) => void;
  onCopy: (c: Corporate) => void;
  onDelete: (c: Corporate) => void;
}

const CorporateCard: React.FC<CorporateCardProps> = ({ corporate, onView, onEdit, onCopy, onDelete }) => {
  const menuItems: ActionMenuItem[] = [
    { id: 'view', label: 'View Details', icon: <IconSystem.eye size={18} />, onClick: () => onView(corporate) },
    { id: 'edit', label: 'Edit', icon: <IconSystem.edit size={18} />, onClick: () => onEdit(corporate) },
    { id: 'copy', label: 'Copy', icon: <IconSystem.copy size={18} />, onClick: () => onCopy(corporate) },
    { id: 'delete', label: 'Delete', icon: <IconSystem.trash size={18} />, onClick: () => onDelete(corporate), variant: 'danger' },
  ];

  const formatDate = (date?: string) => {
    if (!date) return '-';
    try { return new Date(date).toLocaleDateString(); } catch { return '-'; }
  };

  return (
    <div
      style={{
        background: 'var(--bg-primary)', border: '1px solid var(--border-color)', borderRadius: '0.75rem',
        padding: '1.25rem', boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)', transition: 'all 0.2s ease',
        position: 'relative', display: 'flex', flexDirection: 'column', gap: '1rem',
      }}
      onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 4px 12px rgba(37, 99, 235, 0.15)'; }}
      onMouseLeave={(e) => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.08)'; }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: '0.75rem', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: '0.25rem' }}>
            {corporate.corporateCode}
          </div>
          <h3 style={{ fontSize: '1rem', fontWeight: 700, color: 'var(--text-primary)', margin: 0, marginBottom: '0.5rem' }}>
            {corporate.companyName}
          </h3>
        </div>
        <ActionMenu items={menuItems} />
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem', paddingTop: '0.75rem', borderTop: '1px solid var(--border-color)' }}>
        <div>
          <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)', marginBottom: '0.25rem' }}>Industry</div>
          <div style={{ fontSize: '0.85rem', fontWeight: 500, color: 'var(--text-primary)' }}>{corporate.industry || '-'}</div>
        </div>
        <div>
          <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)', marginBottom: '0.25rem' }}>Agreement</div>
          <div style={{ fontSize: '0.85rem', fontWeight: 500, color: 'var(--text-primary)' }}>
            {formatDate(corporate.agreementStart)} - {formatDate(corporate.agreementEnd)}
          </div>
        </div>
        <div>
          <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)', marginBottom: '0.25rem' }}>Discount</div>
          <div style={{ fontSize: '0.85rem', fontWeight: 500, color: 'var(--text-primary)' }}>
            {corporate.discountValue != null ? `${corporate.discountValue}${corporate.discountType === 'Percentage' ? '%' : ''}` : '-'}
          </div>
        </div>
        <div>
          <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)', marginBottom: '0.25rem' }}>Status</div>
          <span className={`badge badge-${corporate.isActive ? 'active' : 'inactive'}`}>
            {corporate.isActive ? 'Active' : 'Inactive'}
          </span>
        </div>
      </div>
    </div>
  );
};

// ============================================================================
// MAIN COMPONENT
// ============================================================================

interface CorporateMasterScreenProps {
  moduleIcon?: string;
  tabId?: string;
  moduleName?: string;
  /**
   * Subset of {'add','edit','delete','view','export'} the current user may
   * perform on this module — driven by admin.tbl_role_permissions
   * (passed through main_layout from MODULES_CONFIG.actions). Defaults to
   * [] (fail-closed): if the prop is missing, no action buttons render.
   */
  allowedActions?: string[];
}

const CorporateMasterScreen: React.FC<CorporateMasterScreenProps> = ({ moduleIcon, tabId, moduleName, allowedActions = [] }) => {
  const { isMinimized, dismiss: dismissMinimized, getMinimizedModal } = useModalMinimize();

  // --- Column visibility ---
  const [visibleColumns, setVisibleColumns] = useState<string[]>(() => {
    return columnDefinitions.filter(col => col.defaultVisible).map(col => col.id);
  });

  // --- Server-side pagination & search state ---
  const [queryParams, setQueryParams] = useState<CorporateSearchRequest>({ page: 1, pageSize: 25 });
  const { data, pagination, refetch } = useCorporates(queryParams);
  const { deleteCorporate, loading: isDeleting } = useDeleteCorporate();

  // --- View state ---
  const [viewMode, setViewMode] = useState<ViewMode>('list');
  const [searchQuery, setSearchQuery] = useState('');
  const [filterOpen, setFilterOpen] = useState(false);

  // --- Filter state ---
  const [filterIndustry, setFilterIndustry] = useState('');
  const [filterDiscountType, setFilterDiscountType] = useState('');
  const [filterStatus, setFilterStatus] = useState('');

  // --- Modal state ---
  const [formModalOpen, setFormModalOpen] = useState(false);
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [searchPopupOpen, setSearchPopupOpen] = useState(false);
  const [formMode, setFormMode] = useState<'create' | 'edit' | 'copy'>('create');
  const [selectedRecord, setSelectedRecord] = useState<Corporate | null>(null);
  const createInstanceCounter = useRef(0);
  const [createInstanceId, setCreateInstanceId] = useState<string>('0');
  const copyInstanceCounter = useRef(0);
  const [copyInstanceId, setCopyInstanceId] = useState<string>('0');
  const formModalMinimizeRef = useRef<(() => void) | null>(null);

  // --- Column selector handlers ---
  const handleApplyColumns = useCallback((newVisibleColumns: string[]) => {
    setVisibleColumns(newVisibleColumns);
    localStorage.setItem('corporate-master-columns', JSON.stringify(newVisibleColumns));
  }, []);

  const handleResetColumns = useCallback(() => {
    const defaultVisible = columnDefinitions.filter(col => col.defaultVisible).map(col => col.id);
    setVisibleColumns(defaultVisible);
    localStorage.removeItem('corporate-master-columns');
  }, []);

  useEffect(() => {
    const saved = localStorage.getItem('corporate-master-columns');
    if (saved) {
      try { setVisibleColumns(JSON.parse(saved)); } catch { /* ignore */ }
    }
  }, []);

  // --- Search debounce ---
  useEffect(() => {
    const timer = setTimeout(() => {
      setQueryParams(prev => ({ ...prev, searchTerm: searchQuery || undefined, page: 1 }));
    }, 400);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // --- Pagination handlers ---
  const handlePageChange = useCallback((pageIndex: number) => {
    setQueryParams(prev => ({ ...prev, page: pageIndex + 1 }));
  }, []);

  const handlePageSizeChange = useCallback((pageSize: number) => {
    setQueryParams(prev => ({ ...prev, pageSize, page: 1 }));
  }, []);

  // --- F2 search popup ---
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'F2' && !searchPopupOpen && !formModalOpen && !viewModalOpen && !deleteModalOpen) {
        e.preventDefault();
        setSearchPopupOpen(true);
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [searchPopupOpen, formModalOpen, viewModalOpen, deleteModalOpen]);

  // --- View handler ---
  const handleView = useCallback((corp: Corporate) => {
    const viewId = tabId ? `${tabId}-view-modal-${corp.id}` : null;
    if (viewId && isMinimized(viewId)) {
      const modal = getMinimizedModal(viewId);
      dismissMinimized(viewId);
      modal?.onRestore?.();
      return;
    }
    setSelectedRecord(corp);
    setViewModalOpen(true);
  }, [tabId, isMinimized, dismissMinimized, getMinimizedModal]);

  const handleRestoreView = useCallback((corporateId: string) => {
    const corp = data.find(c => c.id === corporateId);
    if (corp) { setSelectedRecord(corp); setViewModalOpen(true); }
  }, [data]);

  // --- Edit handler ---
  const handleEdit = useCallback((corp: Corporate) => {
    const modalId = tabId ? `${tabId}-form-modal-edit-${corp.id}` : null;
    if (modalId && isMinimized(modalId)) {
      const modal = getMinimizedModal(modalId);
      dismissMinimized(modalId);
      modal?.onRestore?.();
      return;
    }
    setSelectedRecord(corp);
    setFormMode('edit');
    setFormModalOpen(true);
  }, [tabId, isMinimized, dismissMinimized, getMinimizedModal]);

  // --- Copy handler ---
  const handleCopy = useCallback((corp: Corporate) => {
    if (formModalOpen) formModalMinimizeRef.current?.();
    const newId = String(++copyInstanceCounter.current);
    setCopyInstanceId(newId);
    setSelectedRecord(corp);
    setFormMode('copy');
    setFormModalOpen(true);
  }, [formModalOpen]);

  // --- Add New handler ---
  const handleAddNew = useCallback(() => {
    const newId = String(++createInstanceCounter.current);
    setCreateInstanceId(newId);
    setFormMode('create');
    setSelectedRecord(null);
    setFormModalOpen(true);
  }, []);

  // --- Delete handler ---
  const handleDeleteClick = useCallback((corp: Corporate) => {
    setSelectedRecord(corp);
    setDeleteModalOpen(true);
  }, []);

  const handleConfirmDelete = useCallback(async () => {
    if (!selectedRecord) return;
    try {
      await deleteCorporate(selectedRecord.id as string, selectedRecord.rowVersion);
      setDeleteModalOpen(false);
      notificationService.toast.success(`"${selectedRecord.corporateCode}" has been deleted successfully.`);
      setSelectedRecord(null);
      refetch();
    } catch {
      notificationService.toast.error('An error occurred while deleting. Please try again.');
    }
  }, [selectedRecord, deleteCorporate, refetch]);

  const handleSaveSuccess = useCallback(() => {
    setFormModalOpen(false);
    setSelectedRecord(null);
    refetch();
  }, [refetch]);

  const handleRestoreModal = useCallback((restoredMode: 'create' | 'edit' | 'copy', instanceId?: string, corporateCode?: string) => {
    if (instanceId) {
      if (restoredMode === 'copy') setCopyInstanceId(instanceId);
      else setCreateInstanceId(instanceId);
    }
    if (corporateCode) {
      const corp = data.find(c => c.corporateCode === corporateCode);
      if (corp) setSelectedRecord(corp);
    }
    setFormMode(restoredMode);
    setFormModalOpen(true);
  }, [data]);

  // --- Format helpers ---
  const formatDate = useCallback((val: string | undefined) => {
    if (!val) return '-';
    try { return new Date(val).toLocaleDateString(); } catch { return '-'; }
  }, []);

  // --- Column definitions for SyutGrid ---
  const tableColumns: SyutGridColumn<Corporate>[] = useMemo(() => [
    { id: 'corporateCode', header: 'Corporate Code', accessorKey: 'corporateCode', size: 140, enableSorting: true },
    { id: 'companyName', header: 'Company Name', accessorKey: 'companyName', size: 220, enableSorting: true },
    { id: 'industry', header: 'Industry', accessorKey: 'industry', size: 120, enableSorting: true,
      cell: ({ getValue }) => <span style={{ fontSize: '0.85rem' }}>{(getValue() as string) || '-'}</span>,
    },
    { id: 'contactPerson', header: 'Contact Person', accessorKey: 'contactPerson', size: 160, enableSorting: true,
      cell: ({ getValue }) => <span style={{ fontSize: '0.85rem' }}>{(getValue() as string) || '-'}</span>,
    },
    { id: 'phone', header: 'Phone', accessorKey: 'phone', size: 140,
      cell: ({ getValue }) => <span style={{ fontSize: '0.85rem' }}>{(getValue() as string) || '-'}</span>,
    },
    { id: 'email', header: 'Email', accessorKey: 'email', size: 200,
      cell: ({ getValue }) => <span style={{ fontSize: '0.85rem' }}>{(getValue() as string) || '-'}</span>,
    },
    { id: 'creditDays', header: 'Credit Days', accessorKey: 'creditDays', size: 110, enableSorting: true,
      cell: ({ getValue }) => <span style={{ fontSize: '0.85rem' }}>{(getValue() as number | undefined) ?? '-'}</span>,
    },
    { id: 'creditLimit', header: 'Credit Limit', accessorKey: 'creditLimit', size: 120, enableSorting: true,
      cell: ({ getValue }) => {
        const val = getValue() as number | undefined;
        return <span style={{ fontSize: '0.85rem' }}>{val != null ? val.toFixed(2) : '-'}</span>;
      },
    },
    { id: 'agreementStart', header: 'Agreement Start', accessorKey: 'agreementStart', size: 130, enableSorting: true,
      cell: ({ getValue }) => <span style={{ fontSize: '0.85rem' }}>{formatDate(getValue() as string | undefined)}</span>,
    },
    { id: 'agreementEnd', header: 'Agreement End', accessorKey: 'agreementEnd', size: 130, enableSorting: true,
      cell: ({ getValue }) => <span style={{ fontSize: '0.85rem' }}>{formatDate(getValue() as string | undefined)}</span>,
    },
    { id: 'discountType', header: 'Discount Type', accessorKey: 'discountType', size: 130, enableSorting: true,
      cell: ({ getValue }) => <span style={{ fontSize: '0.85rem' }}>{(getValue() as string) || '-'}</span>,
    },
    { id: 'discountValue', header: 'Discount Value', accessorKey: 'discountValue', size: 120, enableSorting: true,
      cell: ({ getValue }) => {
        const val = getValue() as number | undefined;
        return <span style={{ fontSize: '0.85rem' }}>{val != null ? val.toFixed(2) : '-'}</span>;
      },
    },
    { id: 'isActive', header: 'Status', accessorKey: 'isActive', size: 100,
      cell: ({ getValue }) => {
        const isActive = getValue() as boolean;
        return <span className={`badge badge-${isActive ? 'active' : 'inactive'}`}>{isActive ? 'Active' : 'Inactive'}</span>;
      },
    },
  ], [formatDate]);

  // --- Row actions ---
  const getRowActions = useCallback((corp: Corporate): ActionMenuItem[] => {
    const items: ActionMenuItem[] = [];
    if (allowedActions.includes('view')) {
      items.push({ id: 'view', label: 'View Details', icon: <IconSystem.eye size={18} />, onClick: () => handleView(corp) });
    }
    if (allowedActions.includes('edit')) {
      items.push({ id: 'edit', label: 'Edit', icon: <IconSystem.edit size={18} />, onClick: () => handleEdit(corp) });
    }
    if (allowedActions.includes('add')) {
      items.push({ id: 'copy', label: 'Copy', icon: <IconSystem.copy size={18} />, onClick: () => handleCopy(corp) });
    }
    if (allowedActions.includes('delete')) {
      items.push({ id: 'delete', label: 'Delete', icon: <IconSystem.trash size={18} />, onClick: () => handleDeleteClick(corp), variant: 'danger' });
    }
    return items;
  }, [allowedActions, handleView, handleEdit, handleCopy, handleDeleteClick]);

  // --- View mode options ---
  const viewModeOptions: ViewModeOption[] = [
    { id: 'list', icon: <IconSystem.list size={16} />, label: 'List View', tooltip: 'List View' },
    { id: 'grid', icon: <IconSystem.grid size={16} />, label: 'Grid View', tooltip: 'Grid View' },
  ];

  // --- Action buttons ---
  const actionButtons: ActionButton[] = useMemo(() => {
    const buttons: ActionButton[] = [
      { id: 'search', icon: <IconSystem.search size={18} />, label: 'Search Corporates', onClick: () => setSearchPopupOpen(true), tooltip: 'Search Corporates (F2)', variant: 'icon' },
    ];
    if (allowedActions.includes('add')) {
      buttons.push({ id: 'add', icon: <IconSystem.add size={18} />, label: 'Add Corporate', onClick: handleAddNew, tooltip: 'Add New Corporate', variant: 'icon' });
    }
    buttons.push({ id: 'refresh', icon: <IconSystem.refresh size={18} />, label: 'Refresh', onClick: () => refetch(), tooltip: 'Refresh', variant: 'icon' });
    if (allowedActions.includes('export')) {
      buttons.push({ id: 'export', icon: <IconSystem.download size={18} />, label: 'Export', onClick: () => console.log('Export clicked'), tooltip: 'Export', variant: 'icon' });
    }
    return buttons;
  }, [allowedActions, refetch, handleAddNew]);

  // --- Filter Apply ---
  const handleFilterApply = useCallback(() => {
    setQueryParams(prev => ({
      ...prev,
      industry: filterIndustry || undefined,
      discountType: filterDiscountType || undefined,
      includeInactive: filterStatus === 'true' ? false : true,
      page: 1,
    }));
  }, [filterIndustry, filterDiscountType, filterStatus]);

  // --- Filter Clear ---
  const handleFilterClear = useCallback(() => {
    setFilterIndustry('');
    setFilterDiscountType('');
    setFilterStatus('');
    setQueryParams(prev => ({ ...prev, industry: undefined, discountType: undefined, includeInactive: undefined, page: 1 }));
  }, []);

  // --- RENDER ---
  return (
    <div className="module-master-container">
      <ModuleHeader
        title="Corporate Master"
        description="Manage corporate accounts and discount agreements"
        icon={moduleIcon && (IconSystem as any)[moduleIcon] ? React.createElement((IconSystem as any)[moduleIcon], { size: 18 }) : undefined}
        searchConfig={{ value: searchQuery, onChange: setSearchQuery, placeholder: 'Search corporates...' }}
        viewModeConfig={{ currentMode: viewMode, modes: viewModeOptions, onChange: (mode) => setViewMode(mode as ViewMode) }}
        filterConfig={{ onClick: () => setFilterOpen(!filterOpen), isActive: filterOpen, appliedCount: [filterIndustry, filterDiscountType, filterStatus].filter(Boolean).length }}
        leadingToolbarItems={tabId ? <MinimizedModalStrip tabId={tabId} /> : undefined}
        customToolbarItems={<ColumnSelector columns={columnDefinitions} visibleColumns={visibleColumns} onApply={handleApplyColumns} onReset={handleResetColumns} />}
        actions={actionButtons}
      />

      <div className="module-content" style={{ marginTop: '6px' }}>
        <div className="data-grid-container">
          {viewMode === 'list' && (
            <SyutGrid
              data={data}
              columns={tableColumns}
              enableRowSelection
              enablePagination
              manualPagination
              totalCount={pagination.totalItems}
              currentPage={(queryParams.page ?? 1) - 1}
              pageSize={queryParams.pageSize ?? 25}
              onPageChange={handlePageChange}
              onPageSizeChange={handlePageSizeChange}
              rowActions={getRowActions}
              emptyMessage="No corporates found"
              frozenLeftColumns={['select', 'corporateCode']}
              frozenRightColumns={['actions']}
              visibleColumns={visibleColumns}
            />
          )}

          {viewMode === 'grid' && (() => {
            const currentPage = queryParams.page ?? 1;
            const currentPageSize = queryParams.pageSize ?? 25;
            const totalPages = Math.max(1, Math.ceil(pagination.totalItems / currentPageSize));
            return (
              <div className="syut-grid-component" style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                <div style={{ padding: '1rem', display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(350px, 1fr))', gap: '1.5rem', flex: 1, overflow: 'auto', alignContent: 'start' }}>
                  {data.length === 0 ? (
                    <div style={{ gridColumn: '1 / -1', textAlign: 'center', padding: '3rem', color: 'var(--text-muted)' }}>
                      <IconSystem.folder size={48} style={{ marginBottom: '1rem', opacity: 0.5 }} />
                      <p>No corporates found</p>
                    </div>
                  ) : (
                    data.map((corp) => (
                      <CorporateCard key={corp.id as string} corporate={corp} onView={handleView} onEdit={handleEdit} onCopy={handleCopy} onDelete={handleDeleteClick} />
                    ))
                  )}
                </div>
                {data.length > 0 && (
                  <div className="pagination-footer">
                    <div className="pagination-info">Showing {data.length} of {pagination.totalItems} items</div>
                    <div className="pagination-controls" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <select value={currentPageSize} onChange={(e) => handlePageSizeChange(Number(e.target.value))}
                        style={{ padding: '0.375rem 0.5rem', borderRadius: '0.375rem', border: '1px solid var(--border-color)', fontSize: '0.85rem', marginRight: '1rem', background: 'var(--bg-primary)', color: 'var(--text-primary)' }}>
                        {[10, 25, 50, 100].map((size) => (<option key={size} value={size}>{size} / page</option>))}
                      </select>
                      <button className="page-btn" onClick={() => handlePageChange(0)} disabled={currentPage <= 1}>
                        <IconSystem.chevronLeft size={16} /><IconSystem.chevronLeft size={16} style={{ marginLeft: '-8px' }} />
                      </button>
                      <button className="page-btn" onClick={() => handlePageChange(currentPage - 2)} disabled={currentPage <= 1}>
                        <IconSystem.chevronLeft size={16} />
                      </button>
                      <span style={{ padding: '0 0.75rem', fontSize: '0.85rem', color: 'var(--text-primary)' }}>Page {currentPage} of {totalPages}</span>
                      <button className="page-btn" onClick={() => handlePageChange(currentPage)} disabled={currentPage >= totalPages}>
                        <IconSystem.chevronRight size={16} />
                      </button>
                      <button className="page-btn" onClick={() => handlePageChange(totalPages - 1)} disabled={currentPage >= totalPages}>
                        <IconSystem.chevronRight size={16} /><IconSystem.chevronRight size={16} style={{ marginLeft: '-8px' }} />
                      </button>
                    </div>
                  </div>
                )}
              </div>
            );
          })()}
        </div>

        {/* Filter panel */}
        {filterOpen && <div className="search-panel">
          <div className="search-panel-header">
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.375rem' }}>
              <IconSystem.filter size={14} />
              <h3 className="search-panel-title">Filters</h3>
            </div>
            <button onClick={() => setFilterOpen(false)}
              style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '0.25rem', display: 'flex', alignItems: 'center', color: 'var(--text-muted)', borderRadius: '0.25rem' }}
              title="Close filters">
              <IconSystem.x size={16} />
            </button>
          </div>

          <div className="search-panel-content">
            <div className="filter-section">
              <SyutSelect label="Industry" options={industryFilterOptions} value={filterIndustry}
                onChange={(val: string | string[] | null) => setFilterIndustry((val as string) || '')}
                placeholder="Select industry..." isClearable={false} size="sm" />
            </div>
            <div className="filter-section">
              <SyutSelect label="Discount Type" options={discountTypeFilterOptions} value={filterDiscountType}
                onChange={(val: string | string[] | null) => setFilterDiscountType((val as string) || '')}
                placeholder="Select discount type..." isClearable={false} size="sm" />
            </div>
            <div className="filter-section">
              <SyutSelect label="Status" options={statusOptions} value={filterStatus}
                onChange={(val: string | string[] | null) => setFilterStatus((val as string) || '')}
                placeholder="Select status..." isClearable={false} size="sm" />
            </div>
          </div>

          <div className="filter-actions">
            <button className="btn btn-secondary" onClick={handleFilterClear}>Clear</button>
            <button className="btn btn-primary" onClick={handleFilterApply}>Apply</button>
          </div>
        </div>}
      </div>

      {/* View Modal */}
      <CorporateViewModal isOpen={viewModalOpen} corporate={selectedRecord}
        onClose={() => setViewModalOpen(false)} onEdit={(corp) => handleEdit(corp)}
        tabId={tabId} moduleName={moduleName} onRestoreView={handleRestoreView} />

      {/* Form Modal */}
      <CorporateModal isOpen={formModalOpen} mode={formMode} record={selectedRecord}
        onSave={handleSaveSuccess} onClose={() => setFormModalOpen(false)}
        tabId={tabId} moduleName={moduleName} onRestoreModal={handleRestoreModal}
        createInstanceId={createInstanceId} copyInstanceId={copyInstanceId} minimizeRef={formModalMinimizeRef} />

      {/* Delete Confirm */}
      <ConfirmDialog open={deleteModalOpen} title="Confirm Delete"
        message={`Are you sure you want to delete "${selectedRecord?.corporateCode}"? This action cannot be undone.`}
        confirmLabel="Delete" cancelLabel="Cancel" variant="danger" loading={isDeleting} loadingMessage="Deleting..."
        onConfirm={handleConfirmDelete} onCancel={() => setDeleteModalOpen(false)} />

      {/* Corporate Search Popup (F2) */}
      <SyutSearchGrid<Corporate>
        isOpen={searchPopupOpen} onClose={() => setSearchPopupOpen(false)}
        onSelect={(corp) => handleView(corp)} title="Search Corporates"
        columns={[
          { key: 'corporateCode', header: 'Code', width: '120px', sortable: true },
          { key: 'companyName', header: 'Company Name', width: '220px', sortable: true },
          { key: 'industry', header: 'Industry', width: '120px', sortable: true },
          { key: 'contactPerson', header: 'Contact', width: '150px', sortable: true },
          {
            key: 'isActive', header: 'Status', width: '100px',
            render: (value: any) => (
              <span style={{
                padding: '0.125rem 0.5rem', borderRadius: '4px', fontSize: '0.75rem', fontWeight: 500,
                background: value ? 'var(--badge-active-bg, #dcfce7)' : 'var(--badge-inactive-bg, #fee2e2)',
                color: value ? 'var(--badge-active-color, #166534)' : 'var(--badge-inactive-color, #991b1b)',
              }}>
                {value ? 'Active' : 'Inactive'}
              </span>
            ),
          },
        ]}
        data={data}
        filters={[{ key: 'isActive', label: 'Status', type: 'select', options: [{ value: 'true', label: 'Active' }, { value: 'false', label: 'Inactive' }] }]}
        rowKey="id" enableRecentlyUsed recentlyUsedKey="corporate-master-search"
        searchPlaceholder="Search by code, name, or industry... (F2 to focus)"
      />
    </div>
  );
};

export default CorporateMasterScreen;
